/**
 * Content Studio Hooks
 *
 * React Query hooks for Content Studio operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  extractProduct,
  extractProductManual,
  generateContent,
  getFormats,
  getStyles,
  getSession,
  listSessions,
  type ExtractResponse,
  type GenerateRequest,
  type GenerateResponse,
} from '@/lib/api/content-studio';

// ============================================================================
// Query Keys
// ============================================================================

export const contentStudioKeys = {
  all: ['content-studio'] as const,
  sessions: () => [...contentStudioKeys.all, 'sessions'] as const,
  session: (id: string) => [...contentStudioKeys.all, 'session', id] as const,
  formats: () => [...contentStudioKeys.all, 'formats'] as const,
  styles: () => [...contentStudioKeys.all, 'styles'] as const,
};

// ============================================================================
// Extract Mutation
// ============================================================================

export function useExtractProduct() {
  const queryClient = useQueryClient();

  return useMutation<ExtractResponse, Error, string>({
    mutationFn: extractProduct,
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Produktdaten erfolgreich extrahiert');
        queryClient.invalidateQueries({ queryKey: contentStudioKeys.sessions() });
      } else {
        toast.error(data.error || 'Extraktion fehlgeschlagen');
      }
    },
    onError: (error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });
}

// ============================================================================
// Manual Upload Mutation
// ============================================================================

export interface ManualUploadParams {
  title: string;
  description: string | null;
  price: string | null;
  currency: string;
  imageFile: File;
}

export function useExtractProductManual() {
  const queryClient = useQueryClient();

  return useMutation<ExtractResponse, Error, ManualUploadParams>({
    mutationFn: (params) =>
      extractProductManual(
        params.title,
        params.description,
        params.price,
        params.currency,
        params.imageFile
      ),
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Produkt erfolgreich hochgeladen');
        queryClient.invalidateQueries({ queryKey: contentStudioKeys.sessions() });
      } else {
        toast.error(data.error || 'Upload fehlgeschlagen');
      }
    },
    onError: (error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });
}

// ============================================================================
// Generate Mutation
// ============================================================================

export function useGenerateContent() {
  const queryClient = useQueryClient();

  return useMutation<GenerateResponse, Error, GenerateRequest>({
    mutationFn: generateContent,
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Content erfolgreich generiert');
        queryClient.invalidateQueries({ queryKey: contentStudioKeys.sessions() });
      } else {
        toast.error(data.error || 'Generierung fehlgeschlagen');
      }
    },
    onError: (error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });
}

// ============================================================================
// Session Queries
// ============================================================================

export function useSession(sessionId: string | null) {
  return useQuery({
    queryKey: contentStudioKeys.session(sessionId || ''),
    queryFn: () => getSession(sessionId!),
    enabled: !!sessionId,
    refetchInterval: (query) => {
      // Poll while generating
      const status = query.state.data?.session?.status;
      return status === 'generating' ? 2000 : false;
    },
  });
}

export function useSessions(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...contentStudioKeys.sessions(), { limit, offset }],
    queryFn: () => listSessions(limit, offset),
  });
}

// ============================================================================
// Config Queries
// ============================================================================

export function useFormats() {
  return useQuery({
    queryKey: contentStudioKeys.formats(),
    queryFn: getFormats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useStyles() {
  return useQuery({
    queryKey: contentStudioKeys.styles(),
    queryFn: getStyles,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
